import { useState, useCallback, useRef } from 'react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger, PopoverAnchor } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Badge } from './ui/badge';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '../lib/utils';
import { Label } from './ui/label';

const MultiSelectFilter = ({ label, options, selected, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const handleSelect = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter((s) => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const handleClearAll = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    onChange([]);
  }, [onChange]);

  const handleRemoveOne = useCallback((e, id) => {
    e.preventDefault();
    e.stopPropagation();
    onChange(selected.filter((s) => s !== id));
  }, [onChange, selected]);

  const handleTriggerClick = useCallback((e) => {
    // Only open popover if clicking on the main area, not on X buttons
    const target = e.target;
    const isXButton = target.closest('[data-remove-btn]');
    if (!isXButton) {
      setOpen(prev => !prev);
    }
  }, []);

  const selectedOptions = options.filter((opt) => selected.includes(opt.id));

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <div 
          ref={containerRef}
          className="relative w-full"
          data-testid={testId}
        >
          <div
            role="combobox"
            aria-expanded={open}
            onClick={handleTriggerClick}
            className={cn(
              "flex h-auto min-h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background cursor-pointer",
              "hover:bg-accent hover:text-accent-foreground",
              "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            )}
          >
            <div className="flex flex-wrap gap-1 flex-1 text-left">
              {selected.length === 0 ? (
                <span className="text-muted-foreground">Select {label}...</span>
              ) : selected.length <= 2 ? (
                selectedOptions.map((opt) => (
                  <Badge 
                    key={opt.id} 
                    variant="secondary" 
                    className="rounded-full text-xs flex items-center gap-0.5 pr-0.5"
                  >
                    <span className="truncate max-w-[80px]">{opt.name}</span>
                    <span
                      data-remove-btn="true"
                      role="button"
                      tabIndex={0}
                      className="ml-0.5 p-0.5 rounded-full hover:bg-destructive/20 cursor-pointer inline-flex items-center justify-center"
                      onClick={(e) => handleRemoveOne(e, opt.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleRemoveOne(e, opt.id);
                        }
                      }}
                    >
                      <X className="h-3 w-3 hover:text-destructive" />
                    </span>
                  </Badge>
                ))
              ) : (
                <Badge variant="secondary" className="rounded-full text-xs">
                  {selected.length} selected
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1 ml-2 shrink-0">
              {selected.length > 0 && (
                <span
                  data-remove-btn="true"
                  role="button"
                  tabIndex={0}
                  className="p-0.5 rounded hover:bg-muted cursor-pointer inline-flex items-center justify-center"
                  onClick={handleClearAll}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleClearAll(e);
                    }
                  }}
                >
                  <X className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                </span>
              )}
              <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
            </div>
          </div>
          <PopoverAnchor className="absolute inset-0 pointer-events-none" />
        </div>
        <PopoverContent className="w-[300px] p-0" align="start">
          <Command>
            <CommandInput placeholder={`Search ${label.toLowerCase()}...`} />
            <CommandList>
              <CommandEmpty>No {label.toLowerCase()} found.</CommandEmpty>
              <CommandGroup className="max-h-64 overflow-auto">
                {options.map((option) => (
                  <CommandItem
                    key={option.id}
                    value={option.name}
                    onSelect={() => handleSelect(option.id)}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        selected.includes(option.id) ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {option.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default MultiSelectFilter;
